import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tabs,
  Tab,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Checkbox,
  Typography,
  useTheme,
  useMediaQuery,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useState, useEffect } from 'react';
import { SearchRequest, useConfig } from '../../api/api';
import { useChatClient } from './useChatClient';
import ExoAvatar from '../exo/ExoAvatar';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';

export const ChatNewConversation = ({ open, onClose }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const config = useConfig();
  const { t } = useTranslation();
  const { createConversation, findOrCreateDirect } = useChatClient();

  const [conversationType, setConversationType] = useState('direct');
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open || !config?.searchUrl) return;

    const cookies = new Cookies();
    const prefix = cookies.get('searchPrefix') || '';

    const timeoutId = setTimeout(() => {
      SearchRequest({
        baseUrl: config.searchUrl,
        path: `indexes/${prefix}users/search`,
        method: 'post',
        data: { q: searchQuery || '', limit: 20 },
        onResponse: (res) => {
          setUsers(res.hits || []);
        },
      });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, config?.searchUrl, open]);

  const handleUserToggle = (user) => {
    if (conversationType === 'direct') {
      handleCreateDirect(user.id);
      return;
    }

    setSelectedUsers(prev => {
      const exists = prev.find(u => u.id === user.id);
      if (exists) {
        return prev.filter(u => u.id !== user.id);
      }
      return [...prev, user];
    });
  };

  const handleCreateDirect = async (userId) => {
    setIsLoading(true);
    try {
      await findOrCreateDirect(userId);
      handleClose();
    } catch (error) {
      console.error('Failed to create conversation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (selectedUsers.length < 1 || !groupName.trim()) return;

    setIsLoading(true);
    try {
      await createConversation(
        conversationType,
        selectedUsers.map(u => u.id),
        groupName
      );
      handleClose();
    } catch (error) {
      console.error('Failed to create conversation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelectedUsers([]);
    setGroupName('');
    setConversationType('direct');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          backgroundColor: colors.glass,
          backdropFilter: 'blur(10px)',
          borderRadius: isMobile ? 0 : '16px',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>{t('New Conversation')}</Typography>
        <IconButton onClick={handleClose} size="small" sx={{ color: colors.text, opacity: 0.5 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Tabs
          value={conversationType}
          onChange={(e, v) => {
            setConversationType(v);
            setSelectedUsers([]);
          }}
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{
            mb: 2,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 40,
              fontSize: isMobile ? '0.8rem' : '0.875rem',
              px: isMobile ? 1 : 2,
            },
            '& .Mui-selected': {
              color: colors.info,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: colors.info,
            },
          }}
        >
          <Tab value="direct" label={t('Direct Message')} />
          <Tab value="group" label={t('Group')} />
          <Tab value="channel" label={t('Channel')} />
        </Tabs>

        {conversationType !== 'direct' && (
          <TextField
            fullWidth
            size="small"
            label={conversationType === 'group' ? t('Group Name') : t('Channel Name')}
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          />
        )}

        <TextField
          fullWidth
          size="small"
          placeholder={t('Search users...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.text, opacity: 0.5, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              backgroundColor: colors.bgInput,
              borderRadius: '10px',
              '& fieldset': { border: 'none' },
            },
          }}
        />

        {conversationType !== 'direct' && selectedUsers.length > 0 && (
          <Box className="flex flex-wrap gap-2 mb-2">
            {selectedUsers.map(user => (
              <Box
                key={user.id}
                className="flex items-center gap-1 px-2 py-1 rounded-full"
                sx={{
                  backgroundColor: colors.info + '20',
                  border: `1px solid ${colors.info}40`,
                }}
              >
                <ExoAvatar size="18px" name={user.label} picture={user.image} />
                <Typography variant="caption" sx={{ color: colors.info, fontWeight: 500 }}>
                  {user.label}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <List sx={{ maxHeight: isMobile ? 'calc(100vh - 320px)' : 300, overflow: 'auto', mx: -2 }}>
          {users.map((user) => (
            <ListItemButton
              key={user.id}
              onClick={() => handleUserToggle(user)}
              selected={selectedUsers.some(u => u.id === user.id)}
              sx={{
                borderRadius: '8px',
                mx: 1,
                '&.Mui-selected': {
                  backgroundColor: colors.info + '15',
                },
                '&:hover': {
                  backgroundColor: colors.card,
                },
              }}
            >
              {conversationType !== 'direct' && (
                <Checkbox
                  checked={selectedUsers.some(u => u.id === user.id)}
                  edge="start"
                  size="small"
                  sx={{
                    color: colors.text,
                    opacity: 0.5,
                    '&.Mui-checked': {
                      color: colors.info,
                      opacity: 1,
                    },
                  }}
                />
              )}
              <ListItemAvatar sx={{ minWidth: 48 }}>
                <ExoAvatar size="36px" name={user.label} picture={user.image} />
              </ListItemAvatar>
              <ListItemText
                primary={user.label}
                secondary={user.subLabel || user.email}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                secondaryTypographyProps={{ variant: 'caption', sx: { opacity: 0.6 } }}
              />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>

      {conversationType !== 'direct' && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleClose}
            sx={{ textTransform: 'none', color: colors.text, opacity: 0.7 }}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateGroup}
            disabled={selectedUsers.length < 1 || !groupName.trim() || isLoading}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              backgroundColor: colors.info,
              '&:hover': { backgroundColor: colors.info, opacity: 0.9 },
            }}
          >
            {t('Create')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
